import { useEffect } from "react"
import { useRouter } from "next/router"

import { useAuth } from "@clerk/nextjs"

import {
  getRouteOrganizationSlug,
  parseLegacyOrganizationRoute,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

interface AppLocation {
  asPath: string
  userId: string
}

/**
 * The most recent organization-scoped page outside settings that this tab
 * rendered, as its full `asPath`, with the signed-in user who rendered it.
 * Settings returns here so leaving settings lands where the user entered it,
 * the way the browser back button does.
 *
 * Held in memory only: it is written from a client effect, so a server render
 * and the first client render after a full page load both see `null`.
 */
let lastAppLocation: AppLocation | null = null

function getOrganizationRelativePath(asPath: string): string | null {
  return (
    parseWorkspaceRoute(asPath)?.relativePath ??
    parseLegacyOrganizationRoute(asPath)?.relativePath ??
    null
  )
}

function isSettingsPath(relativePath: string): boolean {
  return relativePath === "/settings" || relativePath.startsWith("/settings/")
}

export function recordAppLocation(asPath: string, userId: string): void {
  if (!getRouteOrganizationSlug(asPath)) {
    return
  }
  const relativePath = getOrganizationRelativePath(asPath)
  if (!relativePath || isSettingsPath(relativePath)) {
    return
  }
  lastAppLocation = { asPath, userId }
}

/**
 * Returns the last recorded app page when the same user recorded it in
 * `organizationSlug`, so a return link never crosses into another organization
 * or carries one session's page into the next.
 */
export function getLastAppLocation(
  organizationSlug: string | undefined,
  userId: string | null | undefined,
): string | null {
  if (
    !organizationSlug ||
    !userId ||
    lastAppLocation?.userId !== userId ||
    getRouteOrganizationSlug(lastAppLocation.asPath) !== organizationSlug
  ) {
    return null
  }
  return lastAppLocation.asPath
}

export function resetLastAppLocation(): void {
  lastAppLocation = null
}

/** Records every settled route so `getLastAppLocation` stays current. */
export function AppLocationRecorder() {
  const router = useRouter()
  const { userId } = useAuth()

  useEffect(() => {
    if (router.isReady && userId) {
      recordAppLocation(router.asPath, userId)
    }
  }, [router.asPath, router.isReady, userId])

  return null
}
